import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Badge } from '@mui/material';
import { Notifications } from '@mui/icons-material';
import ThemeToggle from '@/components/theme/ThemeToggle';
import UserMenu from './UserMenu';
import PlanBadge from './PlanBadge';
import { useAppSelector } from '@/hooks/redux';
import NotificationCenterDrawer from '@/components/notifications/NotificationCenterDrawer';
import { getAlertSummary } from '@/services/notificationApi';

interface TopBarActionsProps {
  /** When true, hides PlanBadge and ThemeToggle (used on mobile farm selector) */
  compact?: boolean;
}

const TopBarActions: React.FC<TopBarActionsProps> = ({ compact }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [backendCount, setBackendCount] = useState(0);

  // Only poll when authenticated (avoids 403 spam before token is loaded)
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  // Real-time unread count from WebSocket-pushed notifications
  const rtUnread = useAppSelector(
    state => state.ui.notifications.filter((n: any) => !n.read).length
  );

  // Fetch active alert count from backend
  const fetchCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const summary = await getAlertSummary();
      setBackendCount(summary?.total_active ?? 0);
    } catch {
      // Ignore — user may not have alerts access yet
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60_000); // Refresh every 60s
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Re-fetch when drawer closes (user may have acknowledged alerts)
  const handleClose = () => {
    setDrawerOpen(false);
    fetchCount();
  };

  // Show the higher of the two counts (backend active alerts or real-time unread)
  const totalCount = Math.max(rtUnread, backendCount);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {!compact && <PlanBadge />}
      {!compact && <ThemeToggle />}
      <IconButton color="inherit" size="small" onClick={() => setDrawerOpen(true)}>
        <Badge badgeContent={totalCount} color="error" max={99}>
          <Notifications />
        </Badge>
      </IconButton>
      <UserMenu />
      <NotificationCenterDrawer open={drawerOpen} onClose={handleClose} />
    </Box>
  );
};

export default TopBarActions;
